import * as React from 'react';
import {useRef} from 'react';

import {
    getCurrentLocale,
    getLocalizedKvm,
    getLocalizedLandOwnership,
    getLocalizedPrice
} from 'features/services/utils/env-helper';
import {
    getConstructionFormName,
    getDisposalFormName,
    getOutdoorSpaceName,
    getParkingStatusName,
    getWindsDirection
} from 'features/services/utils/name-helpers';
import {LOCALE} from 'features/ui/enums';
import {renderInhabitDate} from 'features/services/utils/date-helpers';
import {LandOwnership} from 'features/template/viewModels/ResidenceStage';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {DisposalForm, ObjectStatus, ResidenceType} from 'features/search/filters/enums';

import './style.scss';

import { Facts } from '.';
import { QuickFactsItem } from './item';

interface Props {
    showMore?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- viewModel varies by page type
    viewModel: any;
    energyMarking: string;
    addressString: string;

    onFeeClick?: (feeString: string, index: string) => ()  => void;
    showCoOwningPrices: boolean;
    onCoOwningClick?: (index: string) => () => void;

    renderAreaInfoNorway: (areaType: string) => () => void;
    renderPriceInfoNorway: (areaType: string) => () => void;
    hasTotalPriceExtraData: boolean;
    onSharedCostClick: (sharedCost: string | React.ReactNode | undefined, index) => () => void;
    renderSharedCostInfo?: string;
}

export const ObjectQuickFacts: React.FunctionComponent<Props> = ({
    showMore, viewModel, energyMarking, addressString,
    onFeeClick, showCoOwningPrices, onCoOwningClick,
    renderAreaInfoNorway, renderPriceInfoNorway, hasTotalPriceExtraData, onSharedCostClick, renderSharedCostInfo
}) => {
    const halfPrice = viewModel.price ? (viewModel.price / 2) : 0;

    const priceButtonRef = useRef<HTMLButtonElement>(null);
    const feeButtonRef = useRef<HTMLButtonElement>(null);
    const areaButtonRef = useRef<HTMLButtonElement>(null);
    const sharedCostButtonRef = useRef<HTMLButtonElement>(null);
    const priceInfoNOButtonRef = useRef<HTMLButtonElement>(null);

    const numberOfVisibleItems = 5;

    const priceString = (viewModel.coOwningPossible && showCoOwningPrices)
        ? viewModel.price
            ? getFormattedMessage('jm.components.QuickFacts.coOwningPriceFrom') + ' ' + getLocalizedPrice(halfPrice)
            : getFormattedMessage('jm.components.QuickFacts.coOwningPossibleFact')
        : getLocalizedPrice(viewModel.price);

    const getOutdoorItems = () => {
        const sorted = [...viewModel.outdoorSpaces].sort((a, b) =>
            getOutdoorSpaceName(a.type).localeCompare(getOutdoorSpaceName(b.type))
        );

        let lastType = '';
        let typeIndex = 0;

        return sorted.map((space, i) => {
            typeIndex = lastType === space.type ? typeIndex + 1 : 1;
            lastType = space.type;

            const label =
                getOutdoorSpaceName(space.type) +
                (sorted.filter(x => x.type === space.type).length > 1 ? ` (${typeIndex})` : '');

            let factString = getFormattedMessage("jm.components.QuickFacts.yes");
            if(space.area) {
                factString = getLocalizedKvm(space.area);
                if(space.direction) {
                    factString += `, ${getWindsDirection(space.direction)}`
                }
            } else if(space.direction) {
                factString = getWindsDirection(space.direction)
            }

            return {
                key: `outdoorSpaces-${i}`,
                devCounter: '00', //(16)
                label: label,
                fact: factString,
                condition: viewModel.outdoorSpaces?.length > 0,
            };
        });
    };

    const facts : Facts[] = [
        ...getOutdoorItems(),

        {key: 'address', devCounter: '01', //(26)
            label: getFormattedMessage('jm.components.QuickFacts.address'),
            fact: addressString,
            condition: Boolean(addressString)
        },
        {key: 'architect', devCounter: '02', //(25)
            label: getFormattedMessage('jm.components.QuickFacts.architect'),
            fact: viewModel.architect,
            condition: viewModel.architect
        },
        {key: 'area', devCounter: '03', //(12)
            label: getFormattedMessage('jm.components.QuickFacts.area'),
            fact: viewModel.primaryArea,
            condition: viewModel.primaryArea && !(viewModel.jmnoBraInternal || viewModel.jmnoBraExternal ||
                viewModel.jmnoBraBalcony || viewModel.jmnoBraTotal || viewModel.jmnoTba)
        },
        {key: 'completedYear', devCounter: '04', //(24)
            label: getFormattedMessage('jm.components.QuickFacts.completed'),
            fact: viewModel.completedYear,
            condition: viewModel.completedYear
        },
        {key: 'constructionForm', devCounter: '05',//(21)
            label: getFormattedMessage('jm.components.QuickFacts.constructionForm'),
            fact: getConstructionFormName(viewModel.constructionForm as ResidenceType),
            condition: viewModel.constructionForm
        },
        {key: 'coOwningFee', devCounter: '06',//(48)
            label: getFormattedMessage('jm.components.QuickFacts.coOwningFeeLabel'),
            fact: getFormattedMessage('jm.components.QuickFacts.coOwningFeeFact') + getLocalizedPrice(viewModel.coOwnerFee),
            onClick: onCoOwningClick ? onCoOwningClick('co-owning') : undefined,
            ref: priceButtonRef,
            condition: viewModel.coOwningStage && viewModel.coOwnerFee && viewModel.coOwningObject
        },
        {key: 'disposalForm', devCounter: '07',//(22)
            label: getFormattedMessage('jm.components.QuickFacts.disposalForm'),
            fact: getDisposalFormName(viewModel.disposalForm as DisposalForm),
            condition: viewModel.disposalForm
        },
        {key: 'elevator', devCounter: '08',//(10)
            label: getFormattedMessage('jm.components.QuickFacts.elevator'),
            fact: getFormattedMessage('jm.components.QuickFacts.yes'),
            condition: viewModel.elevator === true
        },
        {key: 'energyMarking', devCounter: '09',//(19)
            label: getFormattedMessage('jm.components.QuickFacts.energyMarking'),
            fact: energyMarking,
            condition: Boolean(energyMarking) && (!viewModel.swanLabel || getCurrentLocale() === LOCALE.NO)
        },
        {key: 'fee', devCounter: '10',//(05)
            label: viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT
                ? getFormattedMessage('jm.components.QuickFacts.communityFee')
                : getFormattedMessage('jm.components.QuickFacts.fee'),
            fact: getLocalizedPrice(viewModel.fee),
            onClick: onFeeClick ? onFeeClick(getLocalizedPrice(viewModel.fee), 'fee') : undefined,
            ref: feeButtonRef,
            condition: viewModel.fee && !viewModel.isSoldOut && viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED
        },

        {key: 'fiCloset', devCounter: '11',//(45)
            label: 'Oma vaatehuone',
            fact: viewModel.jmoyCloset ? 'Kyllä' : 'Ei',
            condition: getCurrentLocale() === LOCALE.FI
        },
        {key: 'fiEnergyClass', devCounter: '12',//(47)
            label: 'Energialuokka',
            fact: viewModel.energyClass,
            condition: viewModel.energyClass
        },
        {key: 'fiFeesTotal', devCounter: '13',//(36)
            label: 'Kaikki vastikkeet yhteensä',
            fact: getLocalizedPrice(viewModel.jmoyTotalPriceFinland),
            tooltip: 'Hoitovastike + pääomavastike 2 + mahdollinen tontinvuokravastike',
            condition: viewModel.jmoyTotalPriceFinland
        },
        {key: 'fiFinancingFee2', devCounter: '14',//(39)
            label: 'Pääomavastike 2',
            fact: getLocalizedPrice(viewModel.financingFee2),
            tooltip: 'Kuukausittainen pääomavastike, kun taloyhtiölainasta maksetaan lyhennys ja korot',
            condition: viewModel.financingFee2
        },
        {key: 'fiLandOwnerShip', devCounter: '15',//(42)
            label: getFormattedMessage('jm.components.QuickFacts.landOwnership'),
            fact: getLocalizedLandOwnership(viewModel.jmoyLandOwning),
            condition: viewModel.jmoyLandOwning
        },
        {key: 'fiLeaseholdersRent', devCounter: '16',//(41)
            label: 'Tontin vuokravastike',
            fact: getLocalizedPrice(viewModel.jmoyLeaseholdersRent),
            tooltip: 'Asunnolle kohdistuva kuukausittainen osuus tontin vuokrasta',
            condition: viewModel.jmoyLeaseholdersRent && (viewModel.jmoyLandOwning === LandOwnership.PART_RENTAL_PLOT || viewModel.jmoyLandOwning === LandOwnership.RENTAL_LOT)
        },
        {key: 'fiLoanPrice', devCounter: '17',//(40)
            label: 'Velkaosuus',
            fact: getLocalizedPrice(viewModel.jmoyLoanPrice),
            tooltip: 'Asunnolle kohdistuva osuus taloyhtiölainasta',
            condition: viewModel.jmoyLoanPrice
        },
        {key: 'fiPlotBuyingShare', devCounter: '18',//(43)
            label: 'Osuus tontin lunastushinnasta',
            fact: getLocalizedPrice(viewModel.jmoyPlotBuyingShare),
            tooltip: 'Arvioitu vuokratontin lunastushinta. Maksettuasi tämän et maksa tontinvuokravastiketta.',
            condition: viewModel.jmoyPlotBuyingShare && viewModel.jmoyLandOwning && viewModel.jmoyLandOwning === LandOwnership.PART_RENTAL_PLOT
        },
        {key: 'fiPlotRentalFeeTotal', devCounter: '19',//(44)
            label: 'Vastikkeet yhteensä',
            fact: getLocalizedPrice(viewModel.plotRentalFeeTotal),
            tooltip: 'Hoitovastike + pääomavastike 1 + mahdollinen tontinvuokravastike',
            condition: viewModel.plotRentalFeeTotal
        },
        {key: 'fiSauna', devCounter: '20',//(46)
            label: 'Oma sauna',
            fact: viewModel.jmoyOwnSauna  ? 'Kyllä' : 'Ei',
            condition: getCurrentLocale() === LOCALE.FI
        },
        {key: 'fiSecondFee', devCounter: '21',//(38)
            label: 'Pääomavastike 1',
            fact: getLocalizedPrice(viewModel.jmoySecondFee),
            tooltip: 'Kuukausittainen pääomavastike silloin, kun taloyhtiölainasta maksetaan vain korkoja',
            condition: viewModel.jmoySecondFee && !viewModel.isSoldOut && viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED
        },
        {key: 'fiTotalAmount', devCounter: '22',//(34)
            label: 'Kokonaishinta',
            fact: getLocalizedPrice(viewModel.jmoyTotalAmount),
            tooltip: 'Velaton hinta + vuokratontin lunastus',
            condition: viewModel.jmoyTotalAmount
        },
        {key: 'fiTotalPrice', devCounter: '23',//(32)
            label: 'Velaton hinta',
            fact: getLocalizedPrice(viewModel.jmoyTotalPrice),
            tooltip: 'Myyntihinta + lainaosuus',
            condition: viewModel.jmoyTotalPrice && !viewModel.isSoldOut && viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED
        },

        {key: 'floor', devCounter: '24',//(09)
            label: getFormattedMessage('jm.components.QuickFacts.floor'),
            fact: viewModel.floor + ' / ' + viewModel.maxFloor,
            condition: (viewModel.floor !== null && typeof viewModel.floor !== 'undefined') && viewModel.maxFloor && !(viewModel.type === ResidenceType.DETACHED_HOUSE || viewModel.type === ResidenceType.HOUSE || viewModel.type === ResidenceType.TOWN_HOUSE || viewModel.type === ResidenceType.SEMI_DETACHED_HOUSE)
        },
        {key: 'inhabitDate', devCounter: '25',//(23)
            label: getFormattedMessage('jm.components.QuickFacts.inhabitDate'),
            fact: renderInhabitDate(
                viewModel.inhabitDate, 
                viewModel.inhabitDateEnd, 
                viewModel.inhabitDateDetermined, 
                false, 
                false, 
                viewModel.inhabitDateRenderFull
            ),
            condition: viewModel.inhabitDate
        },
        {key: 'livingArea', devCounter: '26',//(13)
            label: getFormattedMessage('jm.components.QuickFacts.livingArea'),
            fact: getLocalizedKvm(viewModel.livingArea),
            condition: viewModel.livingArea && !(getCurrentLocale() === LOCALE.NO && (
                viewModel.jmnoBraTotal || viewModel.jmnoBraInternal || viewModel.jmnoTba ||
                viewModel.jmnoBraExternal || viewModel.jmnoBraBalcony))
        },

        {key: 'noBraBalc', devCounter: '27a',//(12c)
            label: getFormattedMessage('jm.components.QuickFacts.noBRAb'),
            fact: getLocalizedKvm(viewModel.jmnoBraBalcony),
            onClick: renderAreaInfoNorway('bra-b'),
            ref: areaButtonRef,
            condition: viewModel.jmnoBraBalcony
        },
        {key: 'noBraE', devCounter: '27b',//(12b)
            label: getFormattedMessage('jm.components.QuickFacts.noBRAe'),
            fact: getLocalizedKvm(viewModel.jmnoBraExternal),
            onClick: renderAreaInfoNorway('bra-e'),
            ref: areaButtonRef,
            condition: viewModel.jmnoBraExternal
        },
        {key: 'noBraI', devCounter: '27c',//(12a)
            label: getFormattedMessage('jm.components.QuickFacts.noBRAi'),
            fact: getLocalizedKvm(viewModel.jmnoBraInternal),
            onClick: renderAreaInfoNorway('bra-i'),
            ref: areaButtonRef,
            condition: viewModel.jmnoBraInternal
        },
        {key: 'noBraTba', devCounter: '27d',//(12e)
            label: getFormattedMessage('jm.components.QuickFacts.noTBA'),
            fact: getLocalizedKvm(viewModel.jmnoTba),
            onClick: renderAreaInfoNorway('tba'),
            ref: areaButtonRef,
            condition: viewModel.jmnoTba
        },
        {key: 'noBraTotal', devCounter: '27e',//(12d)
            label: getFormattedMessage('jm.components.QuickFacts.noBRATotal'),
            fact: getLocalizedKvm(viewModel.jmnoBraTotal),
            onClick: renderAreaInfoNorway('bra-total'),
            ref: areaButtonRef,
            condition: viewModel.jmnoBraTotal
        },
        {key: 'noFellesgjeld', devCounter: '28',//(30a/30b)
            label: (viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)
                ? getFormattedMessage('jm.components.QuickFacts.fellesgjeldOwnership')
                : getFormattedMessage('jm.components.QuickFacts.fellesgjeld'),
            fact: getLocalizedPrice(viewModel.jmnoFellesgjeld),
            condition: viewModel.jmnoFellesgjeld
        },
        /*{key: 'noFellesgjeldOwnership', devCounter: '28b',//(30b)
            label: getFormattedMessage('jm.components.QuickFacts.fellesgjeldOwnership'),
            fact: getLocalizedPrice(viewModel.jmnoFellesgjeld),
            condition: viewModel.jmnoFellesgjeld
        },*/
        {key: 'noInskudd', devCounter: '29',//(29)
            label: getFormattedMessage('jm.components.QuickFacts.inskudd'),
            fact: getLocalizedPrice(viewModel.jmnoInskudd),
            condition: viewModel.jmnoInskudd
        },
        {key: 'noOmkostinger', devCounter: '30',//(31)
            label: getFormattedMessage('jm.components.QuickFacts.omkostninger'),
            fact: getLocalizedPrice(viewModel.otherExpenses),
            onClick: (getCurrentLocale() === LOCALE.NO && hasTotalPriceExtraData)
                ? renderPriceInfoNorway('omkostninger')
                : undefined,
            ref: priceInfoNOButtonRef,
            condition: viewModel.otherExpenses
        },
        {key: 'noSharedCost', devCounter: '31',//(28)
            label: getFormattedMessage('jm.components.QuickFacts.communityFee'),
            fact: getLocalizedPrice(viewModel.jmnoSharedCost),
            onClick: renderSharedCostInfo
                ? onSharedCostClick(<span>{getLocalizedPrice(viewModel.jmnoSharedCost)}</span>, 'no-shared-cost')
                : undefined,
            ref: sharedCostButtonRef,
            condition: viewModel.jmnoSharedCost
        },
        {key: 'noTotalPrice', devCounter: '32',//(03)
            label: getFormattedMessage('jm.components.QuickFacts.totalPrice'),
            fact: viewModel.constructionForm === ResidenceType.APARTMENT && (viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT)
                ? getLocalizedPrice(viewModel.price + viewModel.otherExpenses + viewModel.jmnoFellesgjeld)
                : getLocalizedPrice(viewModel.price + viewModel.otherExpenses),
            condition: viewModel.price && !viewModel.isSoldOut && viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED
        },

        {key: 'objectNumber', devCounter: '33',//(27)
            label: (viewModel.constructionForm === ResidenceType.PLOT)
                ? getFormattedMessage("jm.components.QuickFacts.number")
                : getFormattedMessage("jm.components.QuickFacts.objectNumber"),
            fact: viewModel.apartmentNumber,
            condition: viewModel.apartmentNumber
        },
        {key: 'parkingSpaceStatus', devCounter: '34',//(17)
            label: getFormattedMessage('jm.components.QuickFacts.parkingStatus'),
            fact: getParkingStatusName(viewModel.parkingSpaceStatus),
            condition: viewModel.parkingSpaceStatus && !viewModel.isSoldOut && viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED
        },
        {key: 'plotArea', devCounter: '35',//(15)
            label: getFormattedMessage('jm.components.QuickFacts.plot'),
            fact: getLocalizedKvm(viewModel.plotArea),
            condition: viewModel.plotArea
        },
        {key: 'price', devCounter: '36',//(02)
            label: getFormattedMessage('jm.components.QuickFacts.price'),
            fact: priceString,
            onClick: (viewModel.coOwningPossible && (showCoOwningPrices || getCurrentLocale() === LOCALE.NO)) && onCoOwningClick
                ? onCoOwningClick('price')
                : undefined,
            ref: priceButtonRef,
            tooltip: getCurrentLocale() === LOCALE.FI
                ? 'Ennen muuttoa maksettava kauppahinta'
                : undefined,
            condition: viewModel.price && !viewModel.isSoldOut && viewModel.objectStatus !== ObjectStatus.SOLD && viewModel.objectStatus !== ObjectStatus.BOOKED
        },
        {key: 'rooms', devCounter: '37',//(7/8)
            label: getCurrentLocale() === LOCALE.NO
                ? getFormattedMessage('jm.components.QuickFacts.numberOfRooms')
                : getFormattedMessage('jm.components.QuickFacts.rooms'),
            fact: viewModel.rooms,
            condition: viewModel.rooms
        },
        {key: 'secondaryArea', devCounter: '38',//(14)
            label: getFormattedMessage('jm.components.QuickFacts.secondaryArea'),
            fact: getLocalizedKvm(viewModel.secondaryArea),
            condition: viewModel.secondaryArea
        },
        {key: 'swanLabel', devCounter: '39',//(20)
            label: getFormattedMessage('jm.components.QuickFacts.swanLabel'),
            fact: getFormattedMessage('jm.components.QuickFacts.yes'),
            condition: viewModel.swanLabel
        }
    ];

    // ORDER
    const renderFactsByOrder = (factOrder: string[]) => {
        let factIndex = 0;

        return factOrder.flatMap((orderKey) => {
            const matchedFacts = facts.filter(f => f.key.startsWith(orderKey));

            if (!matchedFacts.length) return [];

            return matchedFacts
                .filter(f => f.condition)
                .map(fact => {
                    factIndex++;

                    return (
                        <QuickFactsItem
                            key={fact.key}
                            index={factIndex.toString()}
                            devCounter={fact.devCounter}
                            itemLabel={fact.label}
                            itemFact={fact.fact}
                            tooltip={fact.tooltip}
                            onClick={fact.onClick}
                            ref={fact.ref}
                            isHidden={!showMore && factIndex > numberOfVisibleItems}
                        />
                    );
                });
        });
    };

    const getOrder = () => {
        //SE
        if(getCurrentLocale() === LOCALE.SV) {
            if(viewModel.constructionForm === ResidenceType.APARTMENT && (viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT || viewModel.disposalForm === DisposalForm.OWNERSHIP)) {
                return [
                    'price',
                    'fee',
                    'coOwningFee',
                    'floor',
                    'outdoorSpaces',
                    'inhabitDate',
                    'rooms',
                    'area',
                    'secondaryArea',
                    'parkingSpaceStatus',
                    'constructionForm',
                    'disposalForm',
                    'elevator',
                    'energyMarking',
                    'swanLabel',
                    'address',
                    'objectNumber',
                ];
            }
            if(viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT) {
                return [
                    'price',
                    'fee',
                    'coOwningFee',
                    'floor',
                    'outdoorSpaces',
                    'inhabitDate',
                    'rooms',
                    'area',
                    'secondaryArea',
                    'constructionForm',
                    'disposalForm',
                    'elevator',
                    'energyMarking',
                    'swanLabel',
                    'address',
                    'objectNumber',
                ];
            }
        }
        // NO
        if(getCurrentLocale() === LOCALE.NO) {
            // Lägenhet
            if(viewModel.constructionForm === ResidenceType.APARTMENT) {
                // Bostadsrätt
                if(viewModel.disposalForm === DisposalForm.CONDOMINIUM) return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'noInskudd',
                    'noFellesgjeld',
                    'noOmkostinger',
                    'noSharedCost',
                    'coOwningFee',
                    'parkingSpaceStatus',
                    'noBraI',
                    'noBraE',
                    'noBraBalc',
                    'noBraTotal',
                    'noBraTba',
                    'area',
                    'livingArea', // p-room
                    'floor',
                    'rooms',
                    'constructionForm',
                    'disposalForm',
                    'inhabitDate',
                    'outdoorSpaces',
                    'elevator',
                    'swanLabel',
                    'energyMarking',
                    'address',
                ];
                // Ägarlägenhet
                if(viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT) return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'noFellesgjeld',
                    'noOmkostinger',
                    'noSharedCost',
                    'coOwningFee',
                    'parkingSpaceStatus',
                    'noBraI',
                    'noBraE',
                    'noBraBalc',
                    'noBraTotal',
                    'noBraTba',
                    'area',
                    'livingArea', // p-room
                    'floor',
                    'rooms',
                    'constructionForm',
                    'disposalForm',
                    'inhabitDate',
                    'outdoorSpaces',
                    'elevator',
                    'swanLabel',
                    'energyMarking',
                    'address',
                ];
            }
            // Hus
            if(viewModel.constructionForm === ResidenceType.HOUSE || viewModel.constructionForm === ResidenceType.DETACHED_HOUSE) {
                // Bostadsrätt
                if(viewModel.disposalForm === DisposalForm.CONDOMINIUM) return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'noInskudd',
                    'noFellesgjeld',
                    'noOmkostinger',
                    'noSharedCost',
                    'coOwningFee',
                    'parkingSpaceStatus',
                    'noBraI',
                    'noBraE',
                    'noBraBalc',
                    'noBraTotal',
                    'noBraTba',
                    'area',
                    'livingArea', // p-room
                    'plotArea',
                    'outdoorSpaces',
                    'rooms',
                    'constructionForm',
                    'disposalForm',
                    'inhabitDate',
                    'swanLabel',
                    'energyMarking',
                    'address',
                ];
                // Äganderätt
                if(viewModel.disposalForm === DisposalForm.OWNERSHIP) return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'noOmkostinger',
                    'noSharedCost',
                    'coOwningFee',
                    'parkingSpaceStatus',
                    'noBraI',
                    'noBraE',
                    'noBraBalc',
                    'noBraTotal',
                    'noBraTba',
                    'area',
                    'livingArea', // p-room
                    'plotArea',
                    //'plotNumber', // missing
                    'outdoorSpaces',
                    'rooms',
                    'constructionForm',
                    'disposalForm',
                    'inhabitDate',
                    'swanLabel',
                    'energyMarking',
                    'address',
                ]
            }
            // Radhus
            if(viewModel.constructionForm === ResidenceType.TOWN_HOUSE || viewModel.constructionForm === ResidenceType.SEMI_DETACHED_HOUSE) {
                // Bostadsrätt
                if(viewModel.disposalForm === DisposalForm.CONDOMINIUM) return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'noInskudd',
                    'noFellesgjeld',
                    'noOmkostinger',
                    'noSharedCost',
                    'coOwningFee',
                    'parkingSpaceStatus',
                    'noBraI',
                    'noBraE',
                    'noBraBalc',
                    'noBraTotal',
                    'noBraTba',
                    'area',
                    'livingArea', // Primary area
                    'plotArea',
                    'outdoorSpaces',
                    'rooms',
                    'constructionForm',
                    'disposalForm',
                    'inhabitDate',
                    'swanLabel',
                    'energyMarking',
                    'address',
                ];
                // Äganderätt
                if(viewModel.disposalForm === DisposalForm.OWNERSHIP || viewModel.disposalForm === DisposalForm.OWNERSHIP_APARTMENT) return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'noOmkostinger',
                    'noSharedCost',
                    'coOwningFee',
                    'parkingSpaceStatus',
                    'noBraI',
                    'noBraE',
                    'noBraBalc',
                    'noBraTotal',
                    'noBraTba',
                    'area',
                    'livingArea', // Primary area
                    'plotArea',
                    //'plotNumber', // missing
                    'outdoorSpaces',
                    'rooms',
                    'constructionForm',
                    'disposalForm',
                    'inhabitDate',
                    'swanLabel',
                    'energyMarking',
                    'address',
                ];
            }
            // Tomt
            if(viewModel.constructionForm === ResidenceType.PLOT) {
                return [
                    'objectNumber',
                    'noTotalPrice',
                    'price',
                    'coOwningFee',
                    'noOmkostinger',
                    'plotArea',
                    'disposalForm',
                    'address',
                ];
            }
        }
        // FI
        if(getCurrentLocale() === LOCALE.FI) {
            return [
                'objectNumber',//1
                'fiTotalAmount',//2
                'fiLandOwnerShip', //3 //Plot ownership
                'floor',//4
                'price',//5
                'fiLoanPrice',//6
                'fiTotalPrice',//7
                'fiPlotBuyingShare', //8 Plot price?
                'area',//9
                'rooms',//10
                'fee',//11
                'fiSecondFee', //12 //Fee 2
                'coOwningFee',
                'fiLeaseholdersRent', // Plot fee
                'fiPlotRentalFeeTotal',//14
                'fiFinancingFee2',//15
                'fiFeesTotal',//16
                'outdoorSpaces',//17
                'fiSauna',//18
                'fiCloset',//19
                'fiEnergyClass',//20
                'swanLabel',//21
                'inhabitDate',//22
                'address',//23
            ];
        }

        return [
            'price',
            'fee',
            'coOwningFee',
            'floor',
            'outdoorSpaces',
            'inhabitDate',
            'plotArea',
            'rooms',
            'area',
            'secondaryArea',
            'constructionForm',
            'disposalForm',
            'elevator',
            'energyMarking',
            'swanLabel',
            'address',
            'objectNumber',
            'architect',
        ];
    };

    const order = getOrder();

    return (
        <dl className="quick-facts__list">
            {renderFactsByOrder(order)}
        </dl>
    )
};
